import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Building2, User, MapPin } from "lucide-react";
import axios from "axios";
import { Spinner } from "../components/ui/shadcn-io/spinner";

interface FormData {
  clientName: string;
  dateOfOnboarding: string;
  gstNumber: string;
  phoneNumber: string;
  company: string;
  industry: string;
  clientType: string;
  notes: string;
  // Address
  street: string;
  city: string;
  state: string;
  pincode: string;
  // Contact Person
  contactPersonName: string;
  contactPersonDesignation: string;
  contactPersonEmail: string;
  contactPersonPhone: string;
  // Billing Details
  sameAsAddress: boolean;
  billingStreet: string;
  billingCity: string;
  billingState: string;
  billingPincode: string;
}

const states = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

export default function AddClient() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingClient, setIsFetchingClient] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState<FormData>({
    clientName: "",
    dateOfOnboarding: new Date().toISOString().split("T")[0],
    gstNumber: "",
    phoneNumber: "",
    company: "",
    industry: "",
    clientType: "individual",
    notes: "",
    // Address
    street: "",
    city: "",
    state: "",
    pincode: "",
    // Contact Person
    contactPersonName: "",
    contactPersonDesignation: "",
    contactPersonEmail: "",
    contactPersonPhone: "",
    // Billing Details
    sameAsAddress: true,
    billingStreet: "",
    billingCity: "",
    billingState: "",
    billingPincode: "",
  });

  // Fetch client data when in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      fetchClientData(id);
    }
  }, [isEditMode, id]);

  const fetchClientData = async (clientId: string) => {
    try {
      setIsFetchingClient(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/clients/${clientId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const client = response.data.data.client;

      // Fill form with existing data
      setFormData({
        clientName: client.clientName || "",
        dateOfOnboarding: client.dateOfOnboarding
          ? client.dateOfOnboarding.split("T")[0]
          : new Date().toISOString().split("T")[0],
        gstNumber: client.gstNumber || "",
        phoneNumber: client.phoneNumber || "",
        company: client.company || "",
        industry: client.industry || "",
        clientType: client.clientType || "individual",
        notes: client.notes || "",
        // Address
        street: client.address?.street || "",
        city: client.address?.city || "",
        state: client.address?.state || "",
        pincode: client.address?.pincode || "",
        // Contact Person
        contactPersonName: client.contactPerson?.name || "",
        contactPersonDesignation: client.contactPerson?.designation || "",
        contactPersonEmail: client.contactPerson?.email || "",
        contactPersonPhone: client.contactPerson?.phone || "",
        // Billing Details
        sameAsAddress: client.billingDetails?.sameAsAddress ?? true,
        billingStreet: client.billingDetails?.billingAddress?.street || "",
        billingCity: client.billingDetails?.billingAddress?.city || "",
        billingState: client.billingDetails?.billingAddress?.state || "",
        billingPincode: client.billingDetails?.billingAddress?.pincode || "",
      });
    } catch (error: any) {
      console.error("Error fetching client:", error);
      setError(error.response?.data?.message || "Failed to fetch client data");
    } finally {
      setIsFetchingClient(false);
    }
  };

  const handleInputChange = (
    field: keyof FormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Validate required fields
      if (
        !formData.clientName ||
        !formData.gstNumber ||
        !formData.contactPersonEmail
      ) {
        setError("Client Name, GST Number, and Contact Email are required");
        return;
      }

      // Prepare the data for submission
      const submitData = {
        clientName: formData.clientName,
        dateOfOnboarding: formData.dateOfOnboarding,
        gstNumber: formData.gstNumber.toUpperCase(),
        phoneNumber: formData.phoneNumber,
        company: formData.company,
        industry: formData.industry,
        clientType: formData.clientType,
        notes: formData.notes,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          country: "India",
        },
        contactPerson: {
          name: formData.contactPersonName,
          designation: formData.contactPersonDesignation,
          email: formData.contactPersonEmail,
          phone: formData.contactPersonPhone,
        },
        billingDetails: {
          sameAsAddress: formData.sameAsAddress,
          billingAddress: formData.sameAsAddress
            ? {}
            : {
                street: formData.billingStreet,
                city: formData.billingCity,
                state: formData.billingState,
                pincode: formData.billingPincode,
                country: "India",
              },
        },
      };

      const token = localStorage.getItem("authToken");
      const url = isEditMode
        ? `${process.env.REACT_APP_BACKEND_URL}api/clients/${id}`
        : `${process.env.REACT_APP_BACKEND_URL}api/clients`;

      const method = isEditMode ? "PUT" : "POST";
      const response = await axios({
        method,
        url,
        data: submitData,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 201 || response.status === 200) {
        const successMessage = isEditMode
          ? "Client updated successfully!"
          : "Client added successfully!";

        navigate("/clients", {
          state: { message: successMessage },
        });
      }
    } catch (error: any) {
      console.error(
        `Error ${isEditMode ? "updating" : "adding"} client:`,
        error
      );
      setError(
        error.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "add"} client`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading screen while fetching client data
  if (isFetchingClient) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <Spinner variant="ellipsis" size={40} className="text-primary" />
            <div className="text-center">
              <h3 className="text-lg font-medium">Loading Client Data</h3>
              <p className="text-sm text-muted-foreground">
                Fetching client information...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 pt-0">
      {/* Header */}
      <div className="flex items-left gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/clients")}
          className="h-9 w-9 p-0"
          disabled={isLoading}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditMode ? "Edit Client" : "Add New Client"}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode
              ? "Update the client details and information"
              : "Fill in the client details and onboarding information"}
          </p>
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-background/80 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 bg-card p-6 rounded-lg border shadow-lg">
            <Spinner variant="ellipsis" size={40} className="text-primary" />
            <div className="text-center">
              <h3 className="text-lg font-medium">
                {isEditMode ? "Updating Client" : "Adding Client"}
              </h3>
              <p className="text-sm text-muted-foreground">
                Please wait while we {isEditMode ? "update" : "save"} the client
                data...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form Container */}
      <div className="flex justify-start">
        <div className="w-full">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-md bg-destructive/15 border border-destructive/20 p-4 text-sm text-destructive">
                <strong>Error:</strong> {error}
              </div>
            )}

            {/* Basic Information */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="clientName" className="text-sm font-medium">
                    Client Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="clientName"
                    value={formData.clientName}
                    onChange={(e) =>
                      handleInputChange("clientName", e.target.value)
                    }
                    required
                    disabled={isLoading}
                    className="h-10"
                    placeholder="Enter client company name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gstNumber" className="text-sm font-medium">
                    GST Number <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="gstNumber"
                    value={formData.gstNumber}
                    onChange={(e) =>
                      handleInputChange(
                        "gstNumber",
                        e.target.value.toUpperCase()
                      )
                    }
                    required
                    disabled={isLoading}
                    className="h-10 font-mono"
                    placeholder="Enter 15-digit GST number"
                    maxLength={15}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-sm font-medium">
                    Our Company <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.company}
                    onValueChange={(value) =>
                      handleInputChange("company", value)
                    }
                    disabled={isLoading}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bot2Do Technologies Pvt. Ltd">
                        Bot2Do Technologies Pvt. Ltd
                      </SelectItem>
                      <SelectItem value="DevAnsh Digital Solutions LLP">
                        DevAnsh Digital Solutions LLP
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber" className="text-sm font-medium">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      handleInputChange("phoneNumber", e.target.value)
                    }
                    required
                    disabled={isLoading}
                    className="h-10"
                    placeholder="Enter 10-digit phone number"
                    maxLength={10}
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="dateOfOnboarding"
                    className="text-sm font-medium"
                  >
                    Date of Onboarding
                  </Label>
                  <Input
                    id="dateOfOnboarding"
                    type="date"
                    value={formData.dateOfOnboarding}
                    onChange={(e) =>
                      handleInputChange("dateOfOnboarding", e.target.value)
                    }
                    disabled={isLoading}
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientType" className="text-sm font-medium">
                    Client Type
                  </Label>
                  <Select
                    value={formData.clientType}
                    onValueChange={(value) =>
                      handleInputChange("clientType", value)
                    }
                    disabled={isLoading}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select client type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="startup">Startup</SelectItem>
                      <SelectItem value="sme">SME</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="industry" className="text-sm font-medium">
                    Industry
                  </Label>
                  <Input
                    id="industry"
                    value={formData.industry}
                    onChange={(e) =>
                      handleInputChange("industry", e.target.value)
                    }
                    disabled={isLoading}
                    className="h-10"
                    placeholder="e.g., Technology, Healthcare, Finance"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Contact Person Information */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Contact Person
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label
                    htmlFor="contactPersonName"
                    className="text-sm font-medium"
                  >
                    Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="contactPersonName"
                    value={formData.contactPersonName}
                    onChange={(e) =>
                      handleInputChange("contactPersonName", e.target.value)
                    }
                    required
                    disabled={isLoading}
                    className="h-10"
                    placeholder="Contact person's full name"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="contactPersonDesignation"
                    className="text-sm font-medium"
                  >
                    Designation <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="contactPersonDesignation"
                    value={formData.contactPersonDesignation}
                    onChange={(e) =>
                      handleInputChange(
                        "contactPersonDesignation",
                        e.target.value
                      )
                    }
                    required
                    disabled={isLoading}
                    className="h-10"
                    placeholder="e.g., CEO, Manager, Director"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="contactPersonEmail"
                    className="text-sm font-medium"
                  >
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="contactPersonEmail"
                    type="email"
                    value={formData.contactPersonEmail}
                    onChange={(e) =>
                      handleInputChange("contactPersonEmail", e.target.value)
                    }
                    required
                    disabled={isLoading}
                    className="h-10"
                    placeholder="contact@company.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="contactPersonPhone"
                    className="text-sm font-medium"
                  >
                    Phone (Optional)
                  </Label>
                  <Input
                    id="contactPersonPhone"
                    value={formData.contactPersonPhone}
                    onChange={(e) =>
                      handleInputChange("contactPersonPhone", e.target.value)
                    }
                    disabled={isLoading}
                    className="h-10"
                    placeholder="Contact person's phone"
                    maxLength={10}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Address Information */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Address Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="street" className="text-sm font-medium">
                    Street Address <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="street"
                    value={formData.street}
                    onChange={(e) =>
                      handleInputChange("street", e.target.value)
                    }
                    required
                    disabled={isLoading}
                    className="h-10"
                    placeholder="Building, street, area"
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-sm font-medium">
                      City <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      required
                      disabled={isLoading}
                      className="h-10"
                      placeholder="City name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-sm font-medium">
                      State <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.state}
                      onValueChange={(value) =>
                        handleInputChange("state", value)
                      }
                      disabled={isLoading}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pincode" className="text-sm font-medium">
                      Pincode <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="pincode"
                      value={formData.pincode}
                      onChange={(e) =>
                        handleInputChange("pincode", e.target.value)
                      }
                      required
                      disabled={isLoading}
                      className="h-10"
                      placeholder="6-digit pincode"
                      maxLength={6}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Information */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">
                  Additional Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-sm font-medium">
                    Notes
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange("notes", e.target.value)}
                    disabled={isLoading}
                    className="min-h-[100px]"
                    placeholder="Any additional notes about the client..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/clients")}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="min-w-[120px]"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Spinner variant="ellipsis" size={20} />
                    <span>{isEditMode ? "Updating..." : "Adding..."}</span>
                  </div>
                ) : isEditMode ? (
                  "Update Client"
                ) : (
                  "Add Client"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
